/**
 * Enhanced Diagnostic Questions v2.1
 * Includes military background, leadership experience, certifications, and aspiration timeline
 *
 * FIXES: Added Q2-Q4 explicitly (were missing in v2.0)
 * NEW: Q3b Leadership Experience, Q4b Existing Certifications
 */

import { Question, DIAGNOSTIC_QUESTIONS } from './diagnosticQuestions';

export const ENHANCED_DIAGNOSTIC_QUESTIONS: Question[] = [
  // Q1 - Core Capability
  {
    id: 'q1_core_capability',
    text: "What's something you've done repeatedly that others find difficult?",
    type: 'single',
    options: [
      {
        id: 'organized',
        text: 'Organized or managed things/people',
        keywords: ['operations', 'project_management', 'logistics', 'coordination']
      },
      {
        id: 'fixed',
        text: 'Fixed or troubleshot technical problems',
        keywords: ['technical_support', 'IT', 'maintenance', 'troubleshooting']
      },
      {
        id: 'created',
        text: 'Created or built things (physical or digital)',
        keywords: ['design', 'development', 'trades', 'construction']
      },
      {
        id: 'taught',
        text: 'Taught or explained complex concepts',
        keywords: ['training', 'customer_success', 'education', 'mentoring']
      },
      {
        id: 'handled_money',
        text: 'Handled money, budgets, or tracked details',
        keywords: ['accounting', 'finance', 'admin', 'bookkeeping']
      },
      {
        id: 'difficult_people',
        text: 'Dealt with difficult people or high-stress situations',
        keywords: ['customer_service', 'HR', 'healthcare', 'crisis_management']
      },
      {
        id: 'analyzed',
        text: 'Analyzed data or improved processes',
        keywords: ['data_analysis', 'business_intelligence', 'qa', 'optimization']
      },
      {
        id: 'care',
        text: 'Provided care or support to others',
        keywords: ['healthcare', 'social_services', 'customer_success', 'caregiving']
      }
    ]
  },

  // Q2 - Problem Solving (from original DIAGNOSTIC_QUESTIONS)
  DIAGNOSTIC_QUESTIONS[1],

  // Q3 - Work Style (from original DIAGNOSTIC_QUESTIONS)
  DIAGNOSTIC_QUESTIONS[2],

  // NEW Q3b - Leadership Experience ⭐
  {
    id: 'q3b_leadership_experience',
    text: 'Have you ever supervised or managed others in a work setting?',
    type: 'single',
    options: [
      {
        id: 'no_leadership',
        text: 'No, I\'ve always worked independently',
        keywords: ['individual_contributor']
      },
      {
        id: 'trained_mentored',
        text: 'Yes, I\'ve trained or mentored others (1-2 people)',
        keywords: ['mentoring', 'training', 'teaching']
      },
      {
        id: 'led_small_team',
        text: 'Yes, I\'ve led a small team (3-10 people)',
        keywords: ['team_lead', 'supervisor', 'coordination']
      },
      {
        id: 'managed_team',
        text: 'Yes, I\'ve managed a team or department (10-25 people)',
        keywords: ['management', 'supervisor', 'operations']
      },
      {
        id: 'managed_multiple',
        text: 'Yes, I\'ve managed multiple teams or a large operation (25+ people)',
        keywords: ['senior_management', 'director', 'operations_manager']
      }
    ],
    encouragementText: "💡 Leadership experience qualifies you for supervisor and management roles with $15-25K higher salaries"
  },

  // Q4 - Natural Strengths (from original DIAGNOSTIC_QUESTIONS)
  DIAGNOSTIC_QUESTIONS[3],

  // NEW Q4b - Existing Certifications ⭐
  {
    id: 'q4b_existing_certifications',
    text: 'Do you already have any professional certifications or licenses? (Select all that apply)',
    type: 'multiple',
    options: [
      {
        id: 'cdl',
        text: 'Commercial Driver\'s License (CDL - Class A, B, or C)',
        keywords: ['transportation', 'logistics', 'driving']
      },
      {
        id: 'security_clearance_civilian',
        text: 'Security clearance (Confidential, Secret, Top Secret)',
        keywords: ['defense', 'government', 'contractor']
      },
      {
        id: 'it_certs',
        text: 'IT certifications (A+, Network+, Security+, CISSP, etc.)',
        keywords: ['IT', 'cybersecurity', 'technical']
      },
      {
        id: 'medical_certs',
        text: 'Medical certifications (EMT, Paramedic, CNA, MA, Phlebotomy)',
        keywords: ['healthcare', 'medical', 'emergency']
      },
      {
        id: 'trades_licenses',
        text: 'Skilled trades licenses (Welding, HVAC, Electrical, Plumbing)',
        keywords: ['trades', 'construction', 'technical']
      },
      {
        id: 'safety_certs',
        text: 'Safety certifications (OSHA 10/30, forklift, HAZMAT, crane operator)',
        keywords: ['safety', 'operations', 'manufacturing']
      },
      {
        id: 'pm_certs',
        text: 'Project management (PMP, CAPM, Scrum Master, Agile)',
        keywords: ['project_management', 'leadership', 'coordination']
      },
      {
        id: 'real_estate',
        text: 'Real estate license or notary public',
        keywords: ['sales', 'real_estate', 'legal']
      },
      {
        id: 'food_service',
        text: 'Food service (ServSafe, Food Handler, Alcohol Service)',
        keywords: ['hospitality', 'food_service', 'restaurant']
      },
      {
        id: 'none',
        text: 'None of these - I don\'t have any certifications yet',
        keywords: []
      }
    ],
    encouragementText: "💡 Certifications can fast-track you into higher-paying roles immediately. We'll prioritize careers where you can start now vs needing training."
  },

  // Q5 - Military Background Detection (2-part conditional question)
  {
    id: 'q5_military_background',
    text: 'Have you served in the military?',
    type: 'two_step',
    substeps: [
      {
        id: 'q5a_served',
        text: 'Have you served in the military?',
        type: 'single',
        options: [
          { id: 'yes', text: 'Yes, I have served', keywords: ['military', 'veteran'] },
          { id: 'no', text: 'No', keywords: [] }
        ]
      },
      {
        id: 'q5b_branch',
        text: 'Which branch did you serve in?',
        type: 'single',
        options: [
          { id: 'army', text: 'Army', keywords: ['military', 'veteran', 'army'] },
          { id: 'navy', text: 'Navy', keywords: ['military', 'veteran', 'navy'] },
          { id: 'air_force', text: 'Air Force', keywords: ['military', 'veteran', 'air_force'] },
          { id: 'marines', text: 'Marines', keywords: ['military', 'veteran', 'marines'] },
          { id: 'coast_guard', text: 'Coast Guard', keywords: ['military', 'veteran', 'coast_guard'] },
          { id: 'space_force', text: 'Space Force', keywords: ['military', 'veteran', 'space_force'] }
        ]
      }
    ],
    encouragementText: "💡 Your military experience is valuable! We'll help translate your skills to civilian careers."
  },

  // CONDITIONAL Q5b - MOS/Rate (if military selected)
  {
    id: 'q5b_military_mos',
    text: 'What was your primary Military Occupational Specialty (MOS) or Rate?',
    type: 'text',
    placeholder: 'e.g., 68W, 25B, ET, 3D0X2, 0311...',
    encouragementText: "💡 Your military training has direct civilian value. We'll show you exactly which careers match your MOS.",
    allowSkip: true,
    skipText: "I don't remember / prefer not to say"
  },

  // CONDITIONAL Q5c - Security Clearance (if military selected)
  {
    id: 'q5c_security_clearance',
    text: 'Do you currently hold a security clearance?',
    type: 'single',
    options: [
      { id: 'no_clearance', text: 'No / Not anymore', keywords: [] },
      { id: 'confidential', text: 'Confidential', keywords: ['clearance'] },
      { id: 'secret', text: 'Secret', keywords: ['clearance'] },
      { id: 'top_secret', text: 'Top Secret', keywords: ['clearance'] },
      { id: 'ts_sci', text: 'TS/SCI', keywords: ['clearance'] }
    ],
    encouragementText: "💡 Active clearances can add $15-50K to your salary in defense/contractor roles."
  },

  // CONDITIONAL Q5d - G.I. Bill (if military selected)
  {
    id: 'q5d_gi_bill',
    text: 'Have you used your G.I. Bill education benefits?',
    type: 'single',
    options: [
      { id: 'not_used', text: 'No - I have full benefits available', keywords: [] },
      { id: 'partially_used', text: 'Partially - I have some benefits remaining', keywords: [] },
      { id: 'fully_used', text: 'Yes - I have used all my benefits', keywords: [] },
      { id: 'not_eligible', text: "I'm not eligible for G.I. Bill benefits", keywords: [] }
    ],
    encouragementText: "💡 Your G.I. Bill is worth up to $73,000 over 36 months. We'll show you how to maximize this for career growth."
  },

  // Q6 - Current Situation
  {
    id: 'q6_current_situation',
    text: "What's your current work situation?",
    type: 'single',
    options: [
      {
        id: 'full_time',
        text: 'Working full-time (want to transition to something better)',
        keywords: []
      },
      {
        id: 'part_time',
        text: 'Working part-time (need more income/hours)',
        keywords: []
      },
      {
        id: 'unemployed',
        text: 'Unemployed and actively looking',
        keywords: []
      },
      {
        id: 'caregiver',
        text: 'Caregiver (need flexible work options)',
        keywords: ['remote', 'flexible']
      },
      {
        id: 'training',
        text: 'In training/school (planning my next step)',
        keywords: []
      },
      {
        id: 'returning',
        text: 'Returning to work after time away',
        keywords: []
      }
    ]
  },

  // Q7 - Income Needs
  {
    id: 'q7_income_needs',
    text: "What's your minimum acceptable salary?",
    type: 'single',
    options: [
      {
        id: '30_40k',
        text: '$30-40K/year (getting started, building experience)',
        keywords: []
      },
      {
        id: '40_55k',
        text: '$40-55K/year (supporting myself comfortably)',
        keywords: []
      },
      {
        id: '55_70k',
        text: '$55-70K/year (supporting a family)',
        keywords: []
      },
      {
        id: '70k_plus',
        text: '$70K+ per year (high earner path)',
        keywords: []
      },
      {
        id: 'not_sure',
        text: "I'm not sure / it depends on the area",
        keywords: []
      }
    ]
  },

  // Q8 - Location (two-step question)
  {
    id: 'q8_location',
    text: 'Where would you like to work?',
    type: 'two_step',
    substeps: [
      {
        id: 'q8a_preferred_location',
        text: 'What type of area would you prefer to work in?',
        type: 'single',
        options: [
          {
            id: 'major_metro',
            text: 'Major metro area',
            keywords: ['major_metro', 'urban']
          },
          {
            id: 'midsize',
            text: 'Mid-size city or suburbs',
            keywords: ['midsize', 'suburban']
          },
          {
            id: 'small_town',
            text: 'Small town or rural area',
            keywords: ['small_town', 'rural']
          },
          {
            id: 'doesnt_matter',
            text: "Doesn't matter - I'm flexible on location",
            keywords: ['flexible', 'any']
          }
        ]
      },
      {
        id: 'q8b_work_arrangement',
        text: 'What work arrangement fits your life?',
        type: 'single',
        options: [
          {
            id: 'onsite_local',
            text: 'On-site, close to where I live now',
            keywords: ['onsite', 'local']
          },
          {
            id: 'onsite_commute',
            text: 'On-site, willing to commute further for the right job',
            keywords: ['onsite', 'commute']
          },
          {
            id: 'hybrid',
            text: 'Hybrid (some remote, some in-office)',
            keywords: ['hybrid', 'flexible']
          },
          {
            id: 'remote',
            text: 'Fully remote (work from home)',
            keywords: ['remote', 'remote_only']
          },
          {
            id: 'relocate',
            text: 'Open to relocating for the right opportunity',
            keywords: ['relocate', 'flexible']
          }
        ]
      }
    ],
    encouragementText: "💡 This helps us match you with jobs that fit your lifestyle. Your preferred work location affects salary ranges and job availability."
  },

  // Q9 - Career Timeline & Aspiration
  {
    id: 'q9_timeline_aspiration',
    text: "What's your timeline for career growth?",
    type: 'single',
    options: [
      {
        id: 'immediate',
        text: 'I need a job now (0-3 months) - show me what I can start immediately',
        keywords: ['immediate', 'urgent']
      },
      {
        id: 'short_term',
        text: 'I can invest 6-12 months in training/certification for a better opportunity',
        keywords: ['certification', 'training']
      },
      {
        id: 'career_builder',
        text: 'I want to build a real career (1-3 years) - willing to go back to school part-time',
        keywords: ['education', 'degree']
      },
      {
        id: 'long_term',
        text: "I'm thinking long-term (3-5 years) - show me paths to high-income careers",
        keywords: ['aspirational', 'long_term']
      },
      {
        id: 'transformation',
        text: "I want to completely transform my life (5-15 years) - show me the full journey",
        keywords: ['aspirational', 'transformation']
      }
    ],
    encouragementText: "💡 We'll show you both immediate opportunities AND long-term pathways. You don't have to choose just one - we'll map both."
  },

  // Q10 - What matters most
  {
    id: 'q10_priorities',
    text: 'What matters most to you in your next career move? (Choose up to 3)',
    type: 'multiple',
    options: [
      {
        id: 'fast_hire',
        text: 'Getting hired quickly',
        keywords: ['urgent', 'immediate']
      },
      {
        id: 'good_salary',
        text: 'Making good money',
        keywords: ['salary', 'income']
      },
      {
        id: 'work_life_balance',
        text: 'Work-life balance',
        keywords: ['balance', 'quality_of_life']
      },
      {
        id: 'growth_potential',
        text: 'Long-term growth potential',
        keywords: ['career_growth', 'advancement']
      },
      {
        id: 'job_security',
        text: 'Job security and stability',
        keywords: ['stability', 'security']
      },
      {
        id: 'meaningful_work',
        text: 'Doing work I care about',
        keywords: ['purpose', 'meaning']
      },
      {
        id: 'remote_flexibility',
        text: 'Remote work or flexibility',
        keywords: ['remote', 'flexible']
      },
      {
        id: 'no_degree',
        text: 'No college degree required',
        keywords: ['no_degree', 'skills_based']
      },
      {
        id: 'veteran_friendly',
        text: 'Veteran-friendly employers',
        keywords: ['veteran', 'military_friendly']
      },
      {
        id: 'hands_on',
        text: 'Working with my hands / physical work',
        keywords: ['trades', 'physical', 'hands_on']
      }
    ]
  },

  // Q11 - Passion Discovery (Phase 3: Passion-to-Career Mapping)
  {
    id: 'q11_passions',
    text: 'What are you passionate about outside of work? (Choose up to 3)',
    type: 'multiple',
    options: [
      { id: 'music', text: '🎵 Music & Audio', keywords: ['music', 'audio', 'creative'] },
      { id: 'visual_arts', text: '🎨 Visual Arts & Design', keywords: ['art', 'design', 'creative'] },
      { id: 'writing', text: '✍️ Writing & Storytelling', keywords: ['writing', 'content', 'creative'] },
      { id: 'film_video', text: '🎬 Film & Video', keywords: ['video', 'film', 'media'] },
      { id: 'gaming', text: '🎮 Gaming', keywords: ['gaming', 'esports', 'technology'] },
      { id: 'technology', text: '💻 Technology & Computers', keywords: ['technology', 'IT', 'coding'] },
      { id: 'fitness', text: '💪 Fitness & Wellness', keywords: ['fitness', 'health', 'wellness'] },
      { id: 'sports', text: '⚽ Sports & Athletics', keywords: ['sports', 'athletics', 'coaching'] },
      { id: 'outdoor', text: '🏕️ Outdoors & Nature', keywords: ['outdoor', 'nature', 'environment'] },
      { id: 'helping', text: '🤝 Helping People', keywords: ['helping', 'social', 'caregiving'] },
      { id: 'teaching', text: '📚 Teaching & Education', keywords: ['teaching', 'education', 'training'] },
      { id: 'building', text: '🔧 Building & Making', keywords: ['building', 'trades', 'hands_on'] },
      { id: 'cooking', text: '🍳 Food & Cooking', keywords: ['food', 'cooking', 'culinary'] },
      { id: 'animals', text: '🐾 Animals & Pets', keywords: ['animals', 'pets', 'veterinary'] },
      { id: 'business', text: '💼 Business & Entrepreneurship', keywords: ['business', 'sales', 'entrepreneurship'] },
      { id: 'travel', text: '✈️ Travel & Culture', keywords: ['travel', 'tourism', 'hospitality'] },
      { id: 'automotive', text: '🚗 Cars & Mechanics', keywords: ['automotive', 'mechanical', 'repair'] },
      { id: 'none', text: "I'm open to anything", keywords: [] }
    ],
    encouragementText: "💡 Your passions can lead to fulfilling careers! We'll show you how to turn what you love into what you do."
  }
];

// Helper to determine which questions to show based on previous answers
export function getConditionalQuestions(answers: Record<string, unknown>): string[] {
  const questionIds: string[] = [
    'q1_core_capability',
    'q2_problem_solving',
    'q3_work_style',
    'q3b_leadership_experience',  // NEW: Always show
    'q4_natural_strengths',
    'q4b_existing_certifications', // NEW: Always show
    'q5_military_background'
  ];

  // If military selected (check the new two-step substep answer), add follow-up questions
  const militaryServed = answers.q5a_served;
  if (militaryServed === 'yes') {
    questionIds.push('q5b_military_mos');
    questionIds.push('q5c_security_clearance');
    questionIds.push('q5d_gi_bill');
  }

  // Add standard questions
  questionIds.push('q6_current_situation');
  questionIds.push('q7_income_needs');
  questionIds.push('q8_location');
  questionIds.push('q9_timeline_aspiration');
  questionIds.push('q10_priorities');

  // Phase 3: Passion discovery question (always show for better career matching)
  questionIds.push('q11_passions');

  return questionIds;
}

// Common MOS codes for autocomplete
export const COMMON_MOS_CODES = [
  // Army
  { code: '68W', label: '68W - Combat Medic Specialist' },
  { code: '68C', label: '68C - Practical Nursing Specialist' },
  { code: '25B', label: '25B - Information Technology Specialist' },
  { code: '25D', label: '25D - Cyber Network Defender' },
  { code: '92A', label: '92A - Automated Logistical Specialist' },
  { code: '88M', label: '88M - Motor Transport Operator' },
  { code: '91B', label: '91B - Wheeled Vehicle Mechanic' },
  { code: '11B', label: '11B - Infantryman' },
  { code: '13B', label: '13B - Cannon Crewmember' },
  { code: '15T', label: '15T - UH-60 Helicopter Repairer' },

  // Navy
  { code: 'HM', label: 'HM - Hospital Corpsman' },
  { code: 'ET', label: 'ET - Electronics Technician' },
  { code: 'IT', label: 'IT - Information Systems Technician' },
  { code: 'CTN', label: 'CTN - Cryptologic Technician Networks' },
  { code: 'FC', label: 'FC - Fire Controlman' },
  { code: 'MM', label: 'MM - Machinist Mate' },

  // Air Force
  { code: '3D0X2', label: '3D0X2 - Cyber Systems Operations' },
  { code: '3D1X2', label: '3D1X2 - Cyber Transport Systems' },
  { code: '4N0X1', label: '4N0X1 - Aerospace Medical Service' },
  { code: '2A6X1', label: '2A6X1 - Aerospace Propulsion' },

  // Marines
  { code: '0311', label: '0311 - Rifleman' },
  { code: '0621', label: '0621 - Radio Operator' },
  { code: '6072', label: '6072 - Cyber Network Operator' },

  // Coast Guard
  { code: 'IT', label: 'IT - Information Systems Technician' },
  { code: 'HS', label: 'HS - Health Services Technician' }
];
